import React, { useState, useEffect, useRef } from 'react'
import { API_URL } from '../constants'
import TransparentLoader from './../navigationComponents/loader/TransparentLoader'
import AreYouSureModal from './../navigationComponents/areYouSureModal/AreYouSureModal'
import './AddNewVehicle.css'
import { useAuth } from '../auth/Auth'
import VehicleInfo from './VehicleInfo'
import { isEmpty, vehicleIconName, vehicleLabel } from '../utilities/generalUtils'
import { ParkingSpotApi } from '../apis/ParkingSpotApi'
import { UserVehicleApi } from '../apis/UserVehicleApi'
import { useData } from '../data/DataProvider'
import { GpModalShell, GpButton, Icon } from '../components/gp'

// Single existing-vehicle row.
function VehicleRow({ vehicle, onClick, onDelete }) {
    const location = vehicle.parkingSpot
        ? `${vehicle.parkingSpot.locationName}: ${vehicle.parkingSpot.name}`
        : 'No spot assigned'
    const plate = vehicleLabel(vehicle)
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '12px 14px',
                background: 'var(--gp-surface)',
                border: '1px solid var(--gp-hairline)',
                borderRadius: 'var(--gp-r-md)',
                cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick ? () => onClick(vehicle) : undefined}
        >
            <div
                style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: 'var(--gp-card)',
                    border: '1px solid var(--gp-hairline)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--gp-brand)',
                    flexShrink: 0,
                }}
            >
                <Icon name={vehicleIconName(vehicle)} size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        font: 'var(--gp-body-med)',
                        color: 'var(--gp-ink)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {vehicle.name}
                </div>
                <div
                    style={{
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-ink-mute)',
                        marginTop: 2,
                    }}
                >
                    {location}
                </div>
                {plate && (
                    <div style={{ marginTop: 8 }}>
                        <span
                            style={{
                                background: 'var(--gp-ink)',
                                color: '#FFFFFF',
                                borderRadius: 999,
                                padding: '5px 10px',
                                font: '500 10px/1 var(--gp-font)',
                                letterSpacing: 0.4,
                                display: 'inline-block',
                            }}
                        >
                            {plate}
                        </span>
                    </div>
                )}
            </div>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                }}
                aria-label={`Delete ${vehicle.name}`}
                style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: '1px solid var(--gp-hairline)',
                    background: 'var(--gp-card)',
                    color: 'var(--gp-danger)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'background 0.15s ease, border-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FDF3F3'
                    e.currentTarget.style.borderColor = 'var(--gp-danger-border)'
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--gp-card)'
                    e.currentTarget.style.borderColor = 'var(--gp-hairline)'
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                >
                    <path
                        d="M2 4.00001H14M5.33333 4.00001V2.66668C5.33333 2.31305 5.47381 1.97392 5.72386 1.72387C5.97391 1.47382 6.31304 1.33334 6.66667 1.33334H9.33333C9.68696 1.33334 10.0261 1.47382 10.2761 1.72387C10.5262 1.97392 10.6667 2.31305 10.6667 2.66668V4.00001M6.66667 7.33334V11.3333M9.33333 7.33334V11.3333M12.6667 4.00001V13.3333C12.6667 13.687 12.5262 14.0261 12.2761 14.2762C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31304 14.6667 3.97391 14.5262 3.72386 14.2762C3.47381 14.0261 3.33333 13.687 3.33333 13.3333V4.00001H12.6667Z"
                        stroke="currentColor"
                        strokeWidth="1.16667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>
        </div>
    )
}

export default function AddNewVehicleModal(props) {
    const auth = useAuth()
    const data = useData()

    const [parkingSpots, setParkingSpots] = useState([])
    const [loading, setLoading] = useState(false)
    const [openAreYouSureModal, setOpenAreYouSureModal] = useState(false)
    const [vehicleEdit, setVehicleEdit] = useState({})
    const [uuidToDelete, setUuidToDelete] = useState()

    // Inline add-vehicle flow state
    const [vin, setVin] = useState('')
    const [lookup, setLookup] = useState(null) // { make, model, year, vehicleUUID }
    const [lookupError, setLookupError] = useState(false)
    const [nickName, setNickName] = useState('')

    // Edit-vehicle save plumbing — VehicleInfo exposes save() via ref
    const editRef = useRef(null)
    const [editSaving, setEditSaving] = useState(false)

    const handleEditSave = async () => {
        if (!editRef.current) return
        setEditSaving(true)
        try {
            await editRef.current.save()
        } finally {
            setEditSaving(false)
        }
    }

    const fetchParkingSpots = async () => {
        if (!data.userProperties?.length) return
        try {
            const result = await ParkingSpotApi.get(data.userProperties[0].uuid)
            setParkingSpots(result)
        } catch (e) {}
    }

    useEffect(() => {
        fetchParkingSpots()
    }, [data.userProperties])

    const resetAddState = () => {
        setVin('')
        setLookup(null)
        setLookupError(false)
        setNickName('')
    }

    const handleClose = () => {
        props.handleClose()
        setVehicleEdit({})
        resetAddState()
    }

    const editVehicle = (vehicle) => {
        setVehicleEdit(vehicle)
    }

    const cancelEdit = () => {
        setVehicleEdit({})
    }

    const askDelete = (uuid) => {
        setUuidToDelete(uuid)
        setOpenAreYouSureModal(true)
    }

    const deleteThisCar = () => {
        setOpenAreYouSureModal(false)
        if (auth.token && uuidToDelete) {
            setLoading(true)
            fetch(API_URL + 'user-vehicle/' + uuidToDelete, {
                method: 'DELETE',
                headers: {
                    Authorization: 'Bearer ' + auth.token,
                    'Content-Type': 'application/json',
                },
            })
                .then((res) => res.json())
                .then(
                    () => {
                        setLoading(false)
                        props.reloadVehicles()
                    },
                    () => setLoading(false)
                )
        }
    }

    const getInfoByVin = async () => {
        setLoading(true)
        setLookupError(false)
        try {
            const res = await fetch(`${API_URL}vin/${vin}`, {
                method: 'GET',
                headers: { Authorization: 'Bearer ' + auth.token },
            })
            if (res.ok) {
                const result = await res.json()
                setLookup({
                    make: result?.make,
                    model: result?.model,
                    year: result?.year,
                    vehicleUUID: result?.vehicleUUID,
                })
            } else {
                setLookupError(true)
            }
        } catch (e) {
            setLookupError(true)
        } finally {
            setLoading(false)
        }
    }

    const saveVehicle = () => {
        if (!lookup?.vehicleUUID || !nickName) return
        setLoading(true)
        UserVehicleApi.post({
            name: nickName,
            vehicleUUID: lookup.vehicleUUID,
            year: lookup.year,
            vin,
            make: lookup.make,
            model: lookup.model,
        })
            .then(() => {
                resetAddState()
                props.reloadVehicles()
            })
            .finally(() => setLoading(false))
    }

    const editing = !isEmpty(vehicleEdit)
    const inLookup = !!lookup
    const vinComplete = vin.length === 17

    let title = 'Manage vehicles'
    if (editing) title = 'Edit vehicle'
    else if (inLookup) title = 'Confirm new vehicle'

    let footer = null
    if (editing) {
        footer = (
            <>
                <GpButton variant="ghost" onClick={cancelEdit} disabled={editSaving}>
                    Cancel
                </GpButton>
                <GpButton
                    variant="primary"
                    onClick={handleEditSave}
                    disabled={editSaving}
                >
                    {editSaving ? 'Saving…' : 'Save'}
                </GpButton>
            </>
        )
    } else if (inLookup) {
        footer = (
            <>
                <GpButton variant="ghost" onClick={resetAddState} disabled={loading}>
                    Back
                </GpButton>
                <GpButton
                    variant="primary"
                    onClick={saveVehicle}
                    disabled={loading || !nickName}
                >
                    {loading ? 'Saving…' : 'Save vehicle'}
                </GpButton>
            </>
        )
    } else {
        footer = (
            <>
                <GpButton variant="ghost" onClick={handleClose}>
                    Cancel
                </GpButton>
                <GpButton
                    variant="primary"
                    onClick={getInfoByVin}
                    disabled={!vinComplete || loading}
                >
                    {loading ? 'Looking up…' : 'Add vehicle'}
                </GpButton>
            </>
        )
    }

    return (
        <>
            <GpModalShell
                open={props.open}
                onClose={handleClose}
                title={title}
                width={560}
                footer={footer}
            >
                {editing ? (
                    <VehicleInfo
                        ref={editRef}
                        vehicle={vehicleEdit}
                        handleCancel={cancelEdit}
                        parkingSpots={parkingSpots}
                        reloadVehicles={props.reloadVehicles}
                    />
                ) : inLookup ? (
                    <div>
                        <div
                            style={{
                                background: 'var(--gp-surface)',
                                border: '1px solid var(--gp-hairline)',
                                borderRadius: 'var(--gp-r-md)',
                                padding: '16px 18px',
                                marginBottom: 18,
                            }}
                        >
                            <div
                                style={{
                                    font: 'var(--gp-micro)',
                                    color: 'var(--gp-ink-mute)',
                                    letterSpacing: 0.4,
                                    textTransform: 'uppercase',
                                    marginBottom: 6,
                                }}
                            >
                                Vehicle found
                            </div>
                            <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>
                                {lookup.year} {lookup.make} {lookup.model}
                            </div>
                        </div>

                        <label
                            style={{
                                display: 'block',
                                font: 'var(--gp-caption)',
                                color: 'var(--gp-ink-mute)',
                                marginBottom: 6,
                            }}
                        >
                            Nickname
                        </label>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '11px 14px',
                                background: 'var(--gp-card)',
                                border: '1px solid var(--gp-hairline)',
                                borderRadius: 'var(--gp-r-md)',
                            }}
                        >
                            <input
                                value={nickName}
                                onChange={(e) => setNickName(e.target.value)}
                                placeholder="e.g. Family car"
                                autoFocus
                                style={{
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    font: '400 14px/1 var(--gp-font)',
                                    color: 'var(--gp-ink)',
                                }}
                            />
                        </div>
                        <div
                            style={{
                                font: 'var(--gp-caption)',
                                color: 'var(--gp-ink-mute)',
                                marginTop: 8,
                            }}
                        >
                            Give this vehicle a name you'll recognise on the dashboard.
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Vehicle list */}
                        {props.vehicles?.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {props.vehicles.map((v) => (
                                    <VehicleRow
                                        key={v.uuid}
                                        vehicle={v}
                                        onClick={editVehicle}
                                        onDelete={() => askDelete(v.uuid)}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Dotted divider */}
                        {props.vehicles?.length > 0 && (
                            <div
                                style={{
                                    margin: '22px 0 18px',
                                    height: 1,
                                    background:
                                        'repeating-linear-gradient(to right, var(--gp-hairline) 0, var(--gp-hairline) 4px, transparent 4px, transparent 8px)',
                                }}
                            />
                        )}

                        {/* Add a new vehicle */}
                        <div>
                            <div
                                style={{
                                    font: '500 14px/1.2 var(--gp-font)',
                                    color: 'var(--gp-ink-mute)',
                                    letterSpacing: 0.5,
                                    textTransform: 'uppercase',
                                    marginBottom: 12,
                                }}
                            >
                                Add a new vehicle
                            </div>
                            <label
                                style={{
                                    display: 'block',
                                    font: 'var(--gp-caption)',
                                    color: 'var(--gp-ink-mute)',
                                    marginBottom: 6,
                                }}
                            >
                                VIN number
                            </label>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '12px 14px',
                                    background: 'var(--gp-card)',
                                    border: '1px solid var(--gp-hairline)',
                                    borderRadius: 'var(--gp-r-md)',
                                }}
                            >
                                <span
                                    style={{ color: 'var(--gp-ink-mute)', display: 'inline-flex' }}
                                >
                                    <Icon name="car" size={20} />
                                </span>
                                <input
                                    value={vin}
                                    onChange={(e) =>
                                        setVin(e.target.value.toUpperCase())
                                    }
                                    placeholder="1HGCM82633A123456"
                                    maxLength={17}
                                    style={{
                                        flex: 1,
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        font: '500 14px/1 var(--gp-mono)',
                                        color: 'var(--gp-ink)',
                                        letterSpacing: 0.5,
                                    }}
                                />
                                {vin.length > 0 && (
                                    <span
                                        style={{
                                            font: 'var(--gp-micro)',
                                            color:
                                                vin.length === 17
                                                    ? 'var(--gp-brand)'
                                                    : 'var(--gp-ink-mute)',
                                        }}
                                    >
                                        {vin.length}/17
                                    </span>
                                )}
                            </div>
                            <div
                                style={{
                                    font: 'var(--gp-caption)',
                                    color: 'var(--gp-ink-mute)',
                                    marginTop: 8,
                                }}
                            >
                                Find your VIN in your vehicle registration or on the driver-side dashboard.
                            </div>
                            {lookupError && (
                                <div
                                    style={{
                                        font: 'var(--gp-caption)',
                                        color: 'var(--gp-danger)',
                                        marginTop: 10,
                                    }}
                                >
                                    We couldn't find a vehicle for that VIN. Double-check and try again.
                                </div>
                            )}
                        </div>

                        {loading && (
                            <div style={{ marginTop: 16 }}>
                                <TransparentLoader />
                            </div>
                        )}
                    </>
                )}
            </GpModalShell>
            <AreYouSureModal
                open={openAreYouSureModal}
                handleClose={() => setOpenAreYouSureModal(false)}
                yesFunction={deleteThisCar}
                question="Are you sure you want to delete this vehicle?"
            />
        </>
    )
}
